import { useState, useEffect } from 'react';
import { knowledgeBaseApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { KbCollection } from '@/types/knowledge-base';

interface EditCollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collection: KbCollection | null;
  onSuccess?: () => void;
}

export function EditCollectionDialog({
  open,
  onOpenChange,
  collection,
  onSuccess,
}: EditCollectionDialogProps) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
  });

  // Populate form when dialog opens with collection data
  useEffect(() => {
    if (open && collection) {
      setForm({
        name: collection.name,
        description: collection.description || '',
      });
    }
  }, [open, collection]);

  const handleSave = async () => {
    if (!collection) return;

    if (!form.name.trim()) {
      toast.error('Collection name is required');
      return;
    }

    try {
      setSaving(true);
      await knowledgeBaseApi.updateCollection(collection.id, {
        name: form.name,
        description: form.description,
      });
      toast.success('Collection updated successfully');
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      console.error('Failed to update collection:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to update collection';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Collection</DialogTitle>
          <DialogDescription>
            Update the collection name and description
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Name *</label>
            <Input
              placeholder="My Collection"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Description</label>
            <Textarea
              placeholder="Optional description for this collection..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
